import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { RequirementConfigurationService } from '@app/shared/services/requirement-configuration.service';
import { AppConsts } from '@shared/AppConsts';
import { SelectItem } from 'primeng/api';
import { Observable, of, ReplaySubject } from 'rxjs';
import { delay, map, tap } from 'rxjs/operators';
import { ApplicationStatus, ApplicationType, ApplicationUploadFilesInput, CreateLiaTypeInput, CreateLiaTypeOutput, CreateOrUpdateLiaAcademicTitleInput, CreateOrUpdateLiaAcademicTitleOutput, CreateOrUpdateLiaContactInput, CreateOrUpdateLiaEmploymentsInput, CreateOrUpdateLiaEmploymentsInWaitingStatusOutput, CreateOrUpdateLiaEmploymentsOutput, CreateOrUpdateLiaInvoiceAddressInput, CreateOrUpdateLiaLanguageSkillInput, CreateOrUpdateLiaPersonOutputDto, CreateOrUpdateLiaPersonWithoutRegisterCheckInput, CreateOrUpdateLiaPersonWithoutRegisterCheckOutputDto, CreateOrUpdateLiaPersonWithRegisterCheckInput, CreateOrUpdateLiaPracticesInput, CreateOrUpdateLiaPracticesOutput, CreateOrUpdateLiaRestrictionsInput, CreateOrUpdateLiaTherapyMethodInput, CreateOrUpdateLiaVaccinationInputDto, CreateOrUpdateLiaBrgRecognitionInput, ExternalSystemEnum, GeneralDocumentGroupDto, GeneralFileDto, GeneralFileInfoDto, GetLiaOutput, LiaAcademicTitleDto, LiaAcademicTitleServiceProxy, LiaBrgRecognitionServiceProxy, LiaBrgDirectorServiceProxy, LiaContactDto, LiaContactServiceProxy, LiaDocumentServiceProxy, LiaDocumentsOutput, LiaDto, LiaEmploymentDto, LiaEmploymentOutput, LiaEmploymentsServiceProxy, LiaInvoiceAddressDto, LiaInvoiceAddressServiceProxy, LiaLanguageSkillDto, LiaLanguageSkillServiceProxy, LiaPersonDto, LiaPersonServiceProxy, LiaPracticeDto, LiaPracticeServiceProxy, LiaProfessionalResponsibilityServiceProxy, LiaRestrictionOutput, LiaRestrictionsServiceProxy, LiaStatus, LiaTherapyMethodDto, LiaTherapyMethodServiceProxy, LiaTypeServiceProxy, LiaVaccinationDto, LiaVaccinationServiceProxy, PersonApplicationSummaryDto, ProfessionalLicenceDto, RequirementDto, RequirementType, SetLiaOnWaitingAndCreateEhcOutput, LiaBrgRecognitionDto, ClaSponsorshipDirectorDto, LiaBrgDirectorDto, UpdateLiaBrgDirectorsInput, UpdateLiaBrgDirectorsOutput, HealthRegisterIdentificationStatus, LinkPersonWithoutGlnToLiaBrgDirectorInput, LiaPersonWithoutRegisterCheckDto,  } from './../../../shared/service-proxies/service-proxies';
import { AppComponentBase } from '@shared/common/app-component-base';
import { ClaSponsorshipServiceProxy } from './../../../shared/service-proxies/service-proxies';

export class LiaInvoiceAddressDetail {
    name!: string | undefined;
    street!: string;
    streetNr!: string | undefined;
    postCode!: string;
    city!: string;
    email!: string;
    countryId!: string | undefined;

    constructor(data?: LiaInvoiceAddressDetail) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property)) {
                    (<any>this)[property] = (<any>data)[property];
                }
            }
        }
    }
}

@Injectable({providedIn: 'root'})
export class LiaService extends AppComponentBase {
    lia: LiaDto;
    liaResultDocuments: GeneralFileInfoDto[];
    onlyPrivateAddressAllowed = false;

    private liasForPersonSubject = new ReplaySubject<PersonApplicationSummaryDto[]>(1);
    readonly liasForPerson$ = this.liasForPersonSubject.asObservable();

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private requirementConfigurationService: RequirementConfigurationService,
        private liaTypeService: LiaTypeServiceProxy,
        private liaPersonService: LiaPersonServiceProxy,
        private liaContactService: LiaContactServiceProxy,
        private liaEmploymentsService: LiaEmploymentsServiceProxy,
        private liaLanguageSkillService: LiaLanguageSkillServiceProxy,
        private liaInvoiceAddressService: LiaInvoiceAddressServiceProxy,
        private liaDocumentService: LiaDocumentServiceProxy,
        private liaAcademicTitleService: LiaAcademicTitleServiceProxy,
        private liaProfessionalResponsibilityService: LiaProfessionalResponsibilityServiceProxy,
        private liaRestrictionService: LiaRestrictionsServiceProxy,
        private liaVaccinationService: LiaVaccinationServiceProxy,
        private liaTherapyMethodService: LiaTherapyMethodServiceProxy,
        private liaPracticeService: LiaPracticeServiceProxy,
        private liaBrgRecognitionService: LiaBrgRecognitionServiceProxy,
        private liaBrgDirectorService: LiaBrgDirectorServiceProxy,
        protected sponsorshipService: ClaSponsorshipServiceProxy,
    ) {
        super(injector);
        console.log('LiaService constructor');
    }

    loadLiasForPerson(gln: string): Observable<PersonApplicationSummaryDto[]> {
        if (gln && this.isGranted('Pages.Authority.Applications.Person')) {
            return this.liaTypeService.getLiasForPerson(gln).pipe(
                tap(lias => this.liasForPersonSubject.next(lias))
            );
        } else {
            this.liasForPersonSubject.next([]);
            return of([]);
        }
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.liaTypeService.getExternalRegisterByProfessionId(professionId);
    }

    getFailedLiaForProfessionAndGln(caseId: string): Observable<GetLiaOutput> {
        return this.liaTypeService.getFailedLiaForProfessionAndGln(caseId);
    }

    createLia(input: CreateLiaTypeInput): Observable<CreateLiaTypeOutput> {
        return this.liaTypeService.createLiaType(input).pipe(tap((output: CreateLiaTypeOutput) => {
            this.lia = null;
        }));
    }

    getLia(caseId: string): Observable<GetLiaOutput> {
        return this.liaTypeService.getLia(caseId).pipe(
            tap((output: GetLiaOutput) => {
                this.lia = output.lia;
                return this.requirementConfigurationService.getRequirementByProfessionAndType(this.lia.professionId, RequirementType.OnlyPrivateAddressAllowed).subscribe((requirement: RequirementDto) => {
                    this.onlyPrivateAddressAllowed = requirement.value === this.codes.yesNo.yes;
                });
            })
        );
    }

    closeLia(): void {
        this.lia = null;
    }

    getMyLias(): Observable<PersonApplicationSummaryDto[]> {
        return this.liaTypeService.getMyLias();
    }

    isInLiaStatus(expectedStatus: LiaStatus): boolean {
        return this.lia.currentStep === expectedStatus;
    }

    get currentStep(): LiaStatus {
        return this.lia.currentStep;
    }

    get isPaperCertificateRequested(): boolean {
        return this.lia.isPaperCertificateRequested;
    }

    getProfessionalLicencePersonIdForApplication(): Observable<ProfessionalLicenceDto> {
        return this.liaTypeService.getProfessionalLicenceForLia(this.applicationService.caseId);
    }

    getEvaluationComments(): Observable<string> {
        return this.liaTypeService.getEvaluationComments(this.applicationService.caseId);
    }

    releaseLia(comment: string): Observable<void> {
        return this.liaTypeService.releaseLia(this.lia.application.caseId, comment);
    }

    getNextStatus(status: LiaStatus): Observable<LiaStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.liaTypeService.getNextStatus(this.applicationService.caseId, status).pipe(map(status => {
            // Authority goes to 'finish review' and not to 'release'
            if (isAuthority && status === LiaStatus.Release) {
                return LiaStatus.FinishReview;
            }
            return status;
        }));
    }

    getStepUrl(nextStep: LiaStatus): string {
        return this.applicationService.getNextStep(LiaService.getUrlForStep(nextStep));
    }

    getFile(step: LiaStatus, id: number): Observable<GeneralFileDto> {
        return this.liaDocumentService.getFile(this.applicationService.caseId, step, id);
    }

    uploadFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaDocumentService.uploadFiles(input);
    }

    deleteFile(step: LiaStatus, id: number): Observable<void> {
        return this.liaDocumentService.deleteFile(this.applicationService.caseId, step, id);
    }

    get caseId(): string {
        return this.lia.application.caseId;
    }

    get status(): ApplicationStatus {
        return this.lia.application.status;
    }

    get profession(): string {
        return this.lia?.professionId;
    }

    get medRegId(): number {
        return this.lia.liaPerson.medRegId;
    }

    get hasMedRegId(): boolean {
        return this.lia.liaPerson.medRegId != null;
    }

    get psyRegId(): number {
        return this.lia.liaPerson.psyRegId;
    }

    get hasPsyRegId(): boolean {
        return this.lia.liaPerson.psyRegId != null;
    }

    get naregId(): number {
        return this.lia.liaPerson.naregId;
    }

    get hasNaregId(): boolean {
        return this.lia.liaPerson.naregId != null;
    }

    get numberOfLinkedEhcApplications(): number {
        return this.lia.numberOfLinkedEhcApplications;
    }

    get hasAnyLinkedEhcUnderConstruction(): boolean {
        const statuses = this.lia?.statusOfLinkedApplications;
        return statuses?.some(x => x === ApplicationStatus.UnderConstruction);
    }

    getPerson(): Observable<LiaPersonDto> {
        return of(this.lia.liaPerson).pipe(delay(1));
    }

    setLiaPersonStepToReview() {
        this.applicationService.setStepToReview(LiaStatus.Person);
    }

    createOrUpdateLiaPersonWithRegisterCheck(input: CreateOrUpdateLiaPersonWithRegisterCheckInput): Observable<CreateOrUpdateLiaPersonOutputDto> {
        this.applicationService.setStepToReview(LiaStatus.Person);
        return this.liaPersonService.createOrUpdateLiaPersonWithRegisterCheck(input).pipe(tap((output: CreateOrUpdateLiaPersonOutputDto) => {
            this.lia.liaPerson = output.liaPerson;
        }));
    }

    createOrUpdateLiaPersonWithoutRegisterCheck(input: CreateOrUpdateLiaPersonWithoutRegisterCheckInput): Observable<CreateOrUpdateLiaPersonWithoutRegisterCheckOutputDto> {
        this.applicationService.setStepToReview(LiaStatus.Person);
        return this.liaPersonService.createOrUpdateLiaPersonWithoutRegisterCheck(input).pipe(tap((output: CreateOrUpdateLiaPersonWithoutRegisterCheckOutputDto) => {
            this.lia.liaPerson = output.liaPerson;
        }));
    }

    acceptRegisterCheck(): Observable<LiaStatus> {
        this.applicationService.setStepToReview(LiaStatus.Person);
        return this.liaPersonService.acceptRegisterCheck(this.caseId).pipe(tap((status: LiaStatus) => {
            this.lia.currentStep = status;
        }));
    }

    getLiaPersonFiles(forceRefresh?: boolean, hasLicenceInAnotherCanton?: boolean, nationality?: string, noRegister?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaPersonService.getLiaPersonDocuments(caseId, LiaStatus.Person, hasLicenceInAnotherCanton, nationality, noRegister).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaPerson.personDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaPerson.personDocuments);
    }

    uploadLiaPersonFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaPersonService.uploadFiles(input);
    }


    get hasLicenceInOtherCanton(): boolean {
        return this.lia.liaPerson.hasLicenceInAnotherCantonId === this.codes.yesNo.yes;
    }

    getRestrictions(): Observable<LiaRestrictionOutput> {
        return of(this.lia.liaRestrictions).pipe(delay(1));
    }

    createOrUpdateLiaRestrictions(input: CreateOrUpdateLiaRestrictionsInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.Restriction);
        return this.liaRestrictionService.createOrUpdateLiaRestrictions(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaRestrictions.restrictions = input.liaRestrictions;
            this.lia.liaRestrictions.liaRestrictionGeneralInformation = input.liaRestrictionGeneralInformation;
            this.lia.currentStep = status;
        }))
            .pipe(map((status: LiaStatus) => null));
    }

    displayHasJudgementInSwissCriminalRecord(): Observable<boolean> {
        return this.liaRestrictionService.displayHasJudgementInSwissCriminalRecord(this.caseId);
    }

    getContact(): Observable<LiaContactDto> {
        return of(this.lia.liaContact).pipe(delay(0));
    }

    createOrUpdateLiaContact(input: CreateOrUpdateLiaContactInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.Contact);
        return this.liaContactService.createOrUpdateLiaContact(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaContact = input.liaContact;
            this.lia.currentStep = status;
        }))
            .pipe(map((status: LiaStatus) => null));
    }

    getAcademicTitles(): Observable<LiaAcademicTitleDto[]> {
        return of(this.lia.liaAcademicTitles.titles);
    }

    createBlankLiaAcademicTitle(): Observable<string> {
        return this.liaAcademicTitleService.createBlankLiaAcademicTitle(this.caseId);
    }

    deleteLiaAcademicTitle(guid: string): Observable<void> {
        return this.liaAcademicTitleService.deleteLiaAcademicTitle(this.caseId, guid);
    }

    createOrUpdateLiaAcademicTitles(input: CreateOrUpdateLiaAcademicTitleInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.AcademicTitle);
        return this.liaAcademicTitleService.createOrUpdateLiaAcademicTitle(input).pipe(tap((output: CreateOrUpdateLiaAcademicTitleOutput) => {
            this.lia.liaAcademicTitles.titles = output.liaAcademicTitles;
            this.lia.currentStep = output.status;
        })).pipe(map(() => null));
    }

    getLiaAcademicTitleFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaAcademicTitleService.getLiaAcademicTitleFiles(caseId, LiaStatus.AcademicTitle).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaAcademicTitles.documents = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaAcademicTitles.documents);
    }

    uploadLiaAcademicTitleFiles(liaAcademicTitleId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaAcademicTitleService.uploadFiles(liaAcademicTitleId, input);
    }

    hasEmploymentB(): boolean {
        return this.lia.liaEmployments?.employments?.length > 0;
    }

    uploadLiaProfessionalResponsibilityFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaProfessionalResponsibilityService.uploadFiles(input);
    }

    getLiaProfessionalResponsibilityFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaProfessionalResponsibilityService.getLiaProfessionalResponsibilityFiles(caseId, LiaStatus.ProfessionalResponsibility).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaProfessionalResponsibilityOutput.documents = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaProfessionalResponsibilityOutput.documents);
    }

    liaProfessionalResponsibilityProceed(): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.ProfessionalResponsibility);
        return this.liaProfessionalResponsibilityService.proceed(this.caseId).pipe(tap((status: LiaStatus) => {
            this.lia.currentStep = status;
        })).pipe(map(() => null));
    }

    getRestrictionFiles(forceRefresh?: boolean, hasJudgement: boolean = false, dentistImplantologyWork: boolean = false, caseId = this.applicationService.caseId ): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaRestrictionService.getLiaRestrictionDocuments(caseId, LiaStatus.Restriction, hasJudgement, dentistImplantologyWork).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaRestrictions.restrictionDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaRestrictions.restrictionDocuments);
    }

    getRestrictionFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaRestrictionService.getFile(this.applicationService.caseId, LiaStatus.Restriction, fileId);
    }

    uploadRestrictionFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaRestrictionService.uploadFiles(input);
    }

    deleteRestrictionFile(fileId: number): Observable<void> {
        return this.liaRestrictionService.deleteFile(this.applicationService.caseId, LiaStatus.Restriction, fileId);
    }

    getVaccination(): Observable<LiaVaccinationDto> {
        return of(this.lia.liaVaccination.vaccination);
    }

    createOrupdateVaccination(input: LiaVaccinationDto): Observable<LiaStatus> {
        this.applicationService.setStepToReview(LiaStatus.Vaccination);
        const dto = new CreateOrUpdateLiaVaccinationInputDto({ caseId: this.applicationService.caseId, liaVaccination: input });
        return this.liaVaccinationService.createOrUpdateLiaVaccination(dto).pipe(tap((status: LiaStatus) => {
            this.lia.liaVaccination.vaccination = input;
            this.lia.currentStep = status;
        }));
    }

    getInvoiceAddress(): Observable<LiaInvoiceAddressDto> {
        return of(this.lia.liaInvoiceAddress).pipe(delay(1));
    }

    getInvoiceAddressDetail(): Observable<LiaInvoiceAddressDetail> {
        let detail: LiaInvoiceAddressDetail;
        if (this.lia.liaInvoiceAddress.typeOfAddressId === this.codes.address.private) {
            let fullName = this.lia.liaContact.titleId !== null ? this.l(this.lia.liaContact.titleId) + ' ' + this.lia.application.name : this.lia.application.name;
            detail = new LiaInvoiceAddressDetail({
                name: fullName,
                street: this.lia.liaContact.street,
                streetNr: this.lia.liaContact.streetNr,
                postCode: this.lia.liaContact.postCode,
                city: this.lia.liaContact.city,
                email: this.lia.liaContact.email,
                countryId: this.lia.liaContact.countryId,
            });
        } else if (this.lia.liaInvoiceAddress.typeOfAddressId === this.codes.address.business) {
            let employment = this.lia.liaEmployments?.employments?.find(x => x.id === this.lia.liaInvoiceAddress.liaEmploymentId);
            if (employment) {
                detail = new LiaInvoiceAddressDetail({
                    name: employment.businessName,
                    street: employment.street,
                    streetNr: employment.streetNr,
                    postCode: employment.postCode,
                    city: employment.city,
                    email: this.lia.liaContact.email,
                    countryId: this.lia.liaContact.countryId,
                });
            } else {
                detail = null;
            }
        } else {
            detail = null;
        }
        return of(detail).pipe(delay(0));
    }

    createAddressDropdownList(onlyPrivateAddressAllowed: boolean = true): SelectItem[] {

        let addresses: SelectItem[] = [];
        addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(this.lia.liaContact) });

        if (!this.onlyPrivateAddressAllowed && !onlyPrivateAddressAllowed) {

            for (let i = 0; i < this.lia.liaEmployments?.employments?.length; i++) {
                // Use employmentB id as item value
                addresses.push({ value: this.lia.liaEmployments.employments[i].id, label: this.l('EmploymentSingular') + ': ' + this.getEmploymentAddressDescription(this.lia.liaEmployments.employments[i]) });
            }

            addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });
        }

        return addresses;
    }

    private getPrivateAddressDescription(contact: LiaContactDto) {
        if (!contact) {
            return '';
        }

        let str = contact.street ?? '';

        if (contact.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', contact.streetNr) : str.concat(contact.streetNr);
        }

        if (contact.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', contact.postCode) : str.concat(contact.postCode);
        }

        if (contact.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', contact.city) : str.concat(contact.city);
        }

        return str;
    }

    private getEmploymentAddressDescription(employment: LiaEmploymentDto) {
        if (employment == null) {
            return '';
        }

        let str = employment.businessName ?? '';

        if (employment.street?.length > 0) {
            str =  str.length > 0 ? str.concat(', ', employment.street) : str.concat(employment.street);
        }

        if (employment.streetNr?.length > 0) {
            str =  str.length > 0 ? str.concat(' ', employment.streetNr) : str.concat(employment.streetNr);
        }

        if (employment.postCode?.length > 0) {
            str = str.length > 0 ? str.concat(', ', employment.postCode) : str.concat(employment.postCode);
        }

        if (employment.city?.length > 0) {
            str = str.length > 0 ? str.concat(' ', employment.city) : str.concat(employment.city);
        }

        return str;
    }



    createOrUpdateLiaInvoiceAddress(input: CreateOrUpdateLiaInvoiceAddressInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.InvoiceAddress);
        return this.liaInvoiceAddressService.createOrUpdateLiaInvoiceAddress(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaInvoiceAddress = input.liaInvoiceAddress;
            this.lia.currentStep = status;
        }))
            .pipe(map((status: LiaStatus) => null));
    }

    //createAddressDropdownListForEhc(liaContact: LiaContactDto, liaEmployments: LiaEmploymentReadOnlyDto[]): SelectItem[] {

    //    var addresses: SelectItem[] = [];

    //    addresses.push({ value: AppConsts.codes.address.private, label: this.l('Address-Private') + ': ' + this.getPrivateAddressDescription(liaContact) });
    //    for (let i = 0; i < liaEmployments.length; i++) {
    //        if (liaEmployments[i].isMarkedForEhc == true) {
    //            addresses.push({ value: liaEmployments[i].id ?? liaEmployments[i].guid, label: this.l('EmploymentSingular') + ': ' + this.getEmploymentAddressDescription(liaEmployments[i]) });
    //        }
    //    }

    //    addresses.push({ value: AppConsts.codes.address.invoice, label: this.l(AppConsts.codes.address.invoice) });

    //    return addresses;
    //}


    checkDocuments(): Observable<LiaStatus[]> {
        return this.liaTypeService.validateThatAllMandatoryDocumentsHaveBeenUploaded(this.caseId);
    }


    static getUrlForStep(step: LiaStatus): string {
        switch (step) {
            case LiaStatus.Person:
                return 'person';
            case LiaStatus.Contact:
                return 'contact';
            case LiaStatus.Restriction:
                return 'restriction';
            case LiaStatus.LanguageSkill:
                return 'language-skills';
            case LiaStatus.Employment:
                return 'employment';
            case LiaStatus.InvoiceAddress:
                return 'invoice-address';
            case LiaStatus.Document:
                return 'document';
            case LiaStatus.AcademicTitle:
                return 'academic-title';
            case LiaStatus.ProfessionalResponsibility:
                return 'professional-responsibility';
            case LiaStatus.TherapyMethod:
                return 'therapy-method';
            case LiaStatus.Vaccination:
                return 'vaccination';
            case LiaStatus.Practice:
                return 'practice';
            case LiaStatus.Recognition:
                return 'recognition';
            case LiaStatus.Supervision:
                return 'supervision';
            case LiaStatus.Release:
                return 'release';
            case LiaStatus.FinishReview:
                return 'finish-review';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    isEligibleForEhc(): Observable<boolean> {
        let ehc90Days = this.lia.application.type === ApplicationType.Lia90Days;
        return this.liaTypeService.isEligibleForEhc(this.lia.professionId, ehc90Days);
    }

    getLiaPersonFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaPersonService.getFile(this.applicationService.caseId, LiaStatus.Person, fileId);
    }

    deleteLiaPersonFile(fileId: number): Observable<void> {
        return this.liaPersonService.deleteFile(this.applicationService.caseId, LiaStatus.Person, fileId);
    }

    getLanguageSkill(): Observable<LiaLanguageSkillDto> {
        return of(this.lia.liaLanguageSkill).pipe(delay(1));
    }

    createOrUpdateLiaLanguageSkill(input: CreateOrUpdateLiaLanguageSkillInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.LanguageSkill);
        return this.liaLanguageSkillService.createOrUpdateLiaLanguageSkill(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaLanguageSkill = input.liaLanguageSkill;
            this.lia.currentStep = status;
        })).pipe(map((status: LiaStatus) => null));
    }
    getLanguageSkillFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaLanguageSkillService.getLiaLanguageSkillDocuments(caseId, LiaStatus.LanguageSkill).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaLanguageSkill.languageSkillDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaLanguageSkill.languageSkillDocuments);
    }

    getLanguageSkillFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaLanguageSkillService.getFile(this.applicationService.caseId, LiaStatus.LanguageSkill, fileId);
    }

    uploadLanguageSkillFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaLanguageSkillService.uploadFiles(input);
    }

    deleteLanguageSkillFile(fileId: number): Observable<void> {
        return this.liaLanguageSkillService.deleteFile(this.applicationService.caseId, LiaStatus.LanguageSkill, fileId);
    }

    getEmployments(): Observable<LiaEmploymentOutput> {
        return of(this.lia.liaEmployments).pipe(delay(1));
    }

    createOrUpdateLiaEmployments(input: CreateOrUpdateLiaEmploymentsInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.Employment);
        return this.liaEmploymentsService.createOrUpdateLiaEmployments(input).pipe(tap((output: CreateOrUpdateLiaEmploymentsOutput) => {
            this.lia.liaEmployments.employments = output.liaEmployments;
            this.lia.liaEmployments.mobileEmployment = output.liaMobileEmployment;
            this.lia.currentStep = output.status;
        }))
            .pipe(map((output: CreateOrUpdateLiaEmploymentsOutput) => null));
    }

    createOrUpdateLiaEmploymentsInWaitingStatus(input: CreateOrUpdateLiaEmploymentsInput): Observable<void> {
        return this.liaEmploymentsService.createOrUpdateLiaEmploymentsWhileWaitingStatus(input).pipe(tap((output: CreateOrUpdateLiaEmploymentsInWaitingStatusOutput) => {
            if (output.isNewInvoiceAddressRequired) {
                this.lia.liaEmployments.employments = output.liaEmployments;
                this.lia.liaEmployments.mobileEmployment = output.liaMobileEmployment;
                this.router.navigate([`${AppConsts.applicantBaseUrl}/lia/${this.applicationService.caseId}/wizard/invoice-address`]);
            } else {
                this.router.navigate([`${AppConsts.applicantBaseUrl}/ehc/${output.ehcCaseId}/wizard/employment`]);
            }
        })).pipe(map(() => null));
    }

    getEmploymentFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId, ): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaEmploymentsService.getLiaEmploymentDocuments(caseId, LiaStatus.Employment).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaEmployments.employmentDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaEmployments.employmentDocuments);
    }

    getEmploymentFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaEmploymentsService.getFile(this.applicationService.caseId, LiaStatus.Employment, fileId);
    }

    uploadEmploymentFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaEmploymentsService.uploadFiles(input);
    }

    deleteEmploymentFile(fileId: number): Observable<void> {
        return this.liaEmploymentsService.deleteFile(this.applicationService.caseId, LiaStatus.Employment, fileId);
    }

    hasAtLeastOneEmployment(): boolean {
        if (this.lia.liaEmployments?.employments?.length > 0 || this.lia.liaEmployments?.mobileEmployment?.hasMobileEmployment) {
            return true;
        }

        return false;
    }

    getTherapyMethodFiles(forceRefresh?: boolean, caseId = this.applicationService.caseId, ): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaTherapyMethodService.getTherapyMethodDocuments(caseId, LiaStatus.TherapyMethod).pipe(tap((output: LiaDocumentsOutput) => {
                this.lia.liaTherapyMethod.therapyMethodDocuments = output.liaDocuments;
            })).pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaTherapyMethod.therapyMethodDocuments);
    }

    getTherapyMethod(): Observable<LiaTherapyMethodDto> {
        return of(this.lia.liaTherapyMethod).pipe(delay(1));
    }

    createOrUpdateLiaTherapyMethod(input: CreateOrUpdateLiaTherapyMethodInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.TherapyMethod);
        return this.liaTherapyMethodService.createOrUpdateTherapyMethod(input).pipe(tap((status: LiaStatus) => {
            this.lia.liaTherapyMethod = input.liaTherapyMethod;
            this.lia.currentStep = status;
        })).pipe(map((status: LiaStatus) => null));
    }

    getTherapyMethodFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaTherapyMethodService.getFile(this.applicationService.caseId, LiaStatus.TherapyMethod, fileId);
    }

    uploadTherapyMethodFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaTherapyMethodService.uploadFiles(input);
    }

    deleteTherapyMethodFile(fileId: number): Observable<void> {
        return this.liaTherapyMethodService.deleteFile(this.applicationService.caseId, LiaStatus.TherapyMethod, fileId);
    }

    setLiaOnWaitingAndCreateEhc(applicantComment: string): Observable<SetLiaOnWaitingAndCreateEhcOutput> {
        return this.liaTypeService.setLiaOnWaitingAndCreateEhc(this.lia.application.caseId, applicantComment);
    }

    hasEmploymentOrPostgraduateTitle(): boolean {
        if (this.lia.liaEmployments?.employments?.length > 0 || this.lia.liaEmployments?.mobileEmployment?.hasMobileEmployment) {
            return true;
        }

        return false;
    }

    getLiaPracticeRecordFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaPracticeService.getFile(this.caseId, LiaStatus.Practice, fileId);
    }

    deleteLiaPracticeRecordFile(fileId: number): Observable<void> {
        return this.liaPracticeService.deleteFile(this.caseId, LiaStatus.Practice, fileId);
    }

    uploadLiaPracticeRecordFiles(liaPracticeRecordId: string, input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaPracticeService.uploadFiles(liaPracticeRecordId, input);
    }

    getLiaPracticeRecordFiles(
        forceRefresh?: boolean,
        caseId = this.caseId,
        hasExemptionAccordingArt37Kvg: boolean | undefined = undefined
    ): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            if (
                this.lia.liaPractice.exemptionAccordingArt37Kvg === true &&
                hasExemptionAccordingArt37Kvg === undefined
            ) {
                hasExemptionAccordingArt37Kvg = true;
            }
            return this.liaPracticeService
                .getLiaPracticeRecordFiles(caseId, hasExemptionAccordingArt37Kvg)
                .pipe(
                    tap((output: LiaDocumentsOutput) => {
                        this.lia.liaPractice.liaPracticeRecordDocuments = output.liaDocuments;
                    })
                )
                .pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
        }
        return of(this.lia.liaPractice.liaPracticeRecordDocuments);
    }

    createBlankLiaPracticeRecord(postgraduateTitleId: string = undefined): Observable<string> {
        return this.liaPracticeService.createBlankLiaPracticeRecord(this.caseId, postgraduateTitleId);
    }

    deleteLiaPracticeRecord(guid: string): Observable<void> {
        return this.liaPracticeService.deleteLiaPracticeRecord(this.caseId, guid);
    }

    getLiaPractice(): Observable<LiaPracticeDto> {
        return this.liaPracticeService.getLiaPractice(this.caseId);
    }

    createOrUpdateLiaPractices(input: CreateOrUpdateLiaPracticesInput): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.Practice);
        return this.liaPracticeService.createOrUpdateLiaPractices(input).pipe(tap((output: CreateOrUpdateLiaPracticesOutput) => {
            this.lia.liaPractice = input.liaPractice;
            this.lia.liaPractice.liaPracticeConfigSnapshot = output.liaPractice.liaPracticeConfigSnapshot;
            this.lia.currentStep = output.status;
        })).pipe(map((output: CreateOrUpdateLiaPracticesOutput) => null));
    }

    createOrUpdateLiaBrgRecognition(input: CreateOrUpdateLiaBrgRecognitionInput): Observable<LiaStatus> {
        this.applicationService.setStepToReview(LiaStatus.Recognition);

        return this.liaBrgRecognitionService.createOrUpdateLiaBrgRecognition(input).pipe(
            tap((status: LiaStatus) => {
                this.lia.currentStep = status;
            })
        );
    }

    /*
    getLiaBrgRecognitionDocuments(forceRefresh?: boolean, caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        if (forceRefresh) {
            return this.liaBrgRecognitionService.getLiaBrgRecognitionDocuments(caseId, undefined).pipe(
                map((output: LiaDocumentsOutput) => output.liaDocuments)
            );
        }
        return of([]);
    }
        */


    getLiaBrgRecognition(caseId = this.applicationService.caseId): Observable<LiaBrgRecognitionDto> {
        return this.liaBrgRecognitionService.getLiaBrgRecognition(caseId, undefined);
    }

    getLiaBrgRecognitionFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaBrgRecognitionService.getFile(this.applicationService.caseId, undefined, fileId);
    }

    uploadLiaBrgRecognitionFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaBrgRecognitionService.uploadFiles(input);
    }

    deleteLiaBrgRecognitionFile(fileId: number): Observable<void> {
        return this.liaBrgRecognitionService.deleteFile(this.applicationService.caseId, undefined, fileId);
    }

    getDirectors(): Observable<LiaBrgDirectorDto[]> {
        return this.liaBrgDirectorService.getDirectors(this.caseId);
    }
    /*
    createPlaceholderDirector(caseId: string): Observable<number> {
        return this.liaBrgDirectorService.createPlaceholderDirector(caseId);
    }
        */

    updateDirectors(
        input: UpdateLiaBrgDirectorsInput
    ): Observable<void> {
        this.applicationService.setStepToReview(LiaStatus.Supervision);

        return this.liaBrgDirectorService.updateDirectors(input).pipe(
            tap(output => {
                this.lia.currentStep = output.status;
            }),
            map(() => null)
        );
    }

    sponsorshipDirectorRegisterSearch(
        id: number,
        gln: string,
        professionId: string
    ): Observable<HealthRegisterIdentificationStatus> {
        return this.sponsorshipService.sponsorshipDirectorRegisterSearch(
            this.caseId,
            id,
            gln,
            professionId
        );
    }

    brgDirectorRegisterSearch(
        id: number,
        gln: string,
        professionId: string
    ): Observable<HealthRegisterIdentificationStatus> {
        return this.liaBrgDirectorService.brgDirectorRegisterSearch(
            this.caseId,
            id,
            gln,
            professionId
        );
    }

    /*
    getLiaBrgDirectorDocuments(caseId = this.applicationService.caseId): Observable<GeneralDocumentGroupDto[]> {
        return this.liaBrgDirectorService
            .getLiaBrgDirectorDocuments(caseId, undefined)
            .pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
    }

    uploadLiaBrgDirectorFiles(input: ApplicationUploadFilesInput): Observable<GeneralFileInfoDto[]> {
        return this.liaBrgDirectorService.uploadFiles(input);
    }

    deleteLiaBrgDirectorFile(fileId: number): Observable<void> {
        return this.liaBrgDirectorService.deleteFile(this.applicationService.caseId, undefined, fileId);
    }
        */
    
    getLiaBrgDirectorFile(fileId: number): Observable<GeneralFileDto> {
        return this.liaBrgDirectorService.getFile(this.applicationService.caseId, undefined, fileId);
    }


    // NEW FIlehandling-----------------------
    getLiaBrgDirectorDocuments(
        liaBrgDirectorId: number,
        caseId = this.applicationService.caseId
    ): Observable<GeneralDocumentGroupDto[]> {
        return this.liaBrgDirectorService
            .getLiaBrgDirectorDocuments(caseId, liaBrgDirectorId)
            .pipe(map((output: LiaDocumentsOutput) => output.liaDocuments));
    }

    uploadLiaBrgDirectorFiles(
        liaBrgDirectorId: number,
        input: ApplicationUploadFilesInput
    ): Observable<GeneralFileInfoDto[]> {
        return this.liaBrgDirectorService.uploadFiles(liaBrgDirectorId, input);
    }

    deleteLiaBrgDirectorFile(fileId: number): Observable<void> {
        return this.liaBrgDirectorService.deleteFile(this.applicationService.caseId, undefined, fileId);
    }

    getLiaBrgEmploymentsWithDirectors(): Observable<any[]> {
        return this.getDirectors().pipe(
            map((directors: LiaBrgDirectorDto[]) => this.lia.liaEmployments.employments.map(employment => ({
                id: employment.id,
                businessName: employment.businessName,
                directors: directors.filter(d => d.liaEmploymentId === employment.id)
            })))
        );
    }
    createPlaceholderDirector(caseId: string, liaEmploymentId?: number): Observable<number> {
        return this.liaBrgDirectorService.createPlaceholderDirector(caseId, liaEmploymentId);
    }

    linkPersonWithoutGln(caseId: string, directorId: number, personId: number): Observable<void> {
        const input = new LinkPersonWithoutGlnToLiaBrgDirectorInput({
            caseId,
            directorId,
            personId
        });
        return this.liaBrgDirectorService.linkPersonWithoutGln(input);
    }

    createAndAssignPersonWithoutGln(
        caseId: string,
        directorId: number
    ): Observable<void> {
        return this.liaBrgDirectorService.createAndAssignPersonWithoutGln(
            caseId,
            directorId
        );
    }

    getPersonWithoutRegisterCheck(): Observable<LiaPersonWithoutRegisterCheckDto> {
        return this.liaPersonService.getLiaPersonWithoutRegisterCheck(this.caseId);
    }


}

